import { observer } from 'mobx-react-lite'
import { useState, useCallback, useMemo } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { SkillCategory, SkillSet } from '../../../skill-assessment/model/skill-assessment-model'
import { SkillCategoryCard } from '../organism/SkillCategoryCard'
import { CategorySelectionCard } from '../molecule/CategorySelectionCard'
import { CategoryRatingModal } from './CategoryRatingModal'
import { SkillRatingModalContext } from '../../hooks/useSkillRatingModal'
import { SkillAssessmentLevels } from 'src/feature/skill-assessment/component/SkillAssessmentLevels'

/**
 * Props for SkillCategoryList component
 */
interface SkillCategoryListProps {
  categories: SkillCategory[]
  errorMessage: string
}

/**
 * Renders a list of skill category cards with modal management
 * Handles skill assessment modal state and provides context to child components
 */
export const SkillCategoryList = observer(({
  categories,
  errorMessage,
}: SkillCategoryListProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [skillAssessmentSetIndex, setSkillAssessmentSetIndex] = useState<number>(0)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<SkillCategory | null>(null)

  const hasMultipleCategories = categories && categories.length > 1

  const checkIfAllSkillsAreRated = (categoryId: string): boolean => {
    const category = categories?.find(cat => cat.id === categoryId)

    return category?.skills?.every((skill: SkillSet) => skill.selectedSetLevel !== null) ?? false
  }

  const handleRateClick = useCallback((categoryId: string) => {
    const category = categories?.find(cat => cat.id === categoryId)

    if (hasMultipleCategories) {
      // For multiple categories, open the rating modal
      setSelectedCategory(category || null)
    } else {
      // For single category, open assessment directly
      setSkillAssessmentSetIndex(0)
      setSelectedCategoryId(categoryId)
      setIsModalOpen(true)
    }
  }, [categories, hasMultipleCategories])

  const handleModalClose = useCallback(() => {
    setSelectedCategory(null)
    setIsModalOpen(false)
  }, [categories])

  // For single category view - show full detail card
  if (!hasMultipleCategories && categories?.[0]) {
    const category = categories[0]
    const modalContext = useMemo(() => ({
      handleRateSkillClick: (selectedSkillSetIndex: number) => {
        setSkillAssessmentSetIndex(selectedSkillSetIndex)
        setSelectedCategoryId(category.id)
        setIsModalOpen(true)
      },
    }), [category.id])

    return (
      <SkillRatingModalContext.Provider value={modalContext}>
        <SkillCategoryCard
          categoryId={category.id}
          categoryName={category.category_name}
          categoryDescription={category.category_description}
          skills={category.skills}
          isAllSkillsRated={checkIfAllSkillsAreRated(category.id)}
          errorMessage={errorMessage}
          onRateClick={handleRateClick}
        />
        {isModalOpen && selectedCategoryId === category.id && (
          <SkillAssessmentLevels
            skillAssessmentSetIndex={skillAssessmentSetIndex}
            isModalOpen={isModalOpen}
            isModalClose={handleModalClose}
            categoryId={category.id}
          />
        )}
      </SkillRatingModalContext.Provider>
    )
  }

  // For multiple categories view - show selection cards in grid
  return (
    <>
      <AppBox sx={styles.container}>
        {categories?.map(category => (
          <CategorySelectionCard
            key={category.id}
            categoryId={category.id}
            categoryName={category.category_name}
            categoryDescription={category.category_description}
            skillCount={category.skills?.length || 0}
            isAllSkillsRated={checkIfAllSkillsAreRated(category.id)}
            onRateClick={handleRateClick}
          />
        ))}
      </AppBox>
      {selectedCategory && (
        <CategoryRatingModal
          category={selectedCategory}
          isOpen={Boolean(selectedCategory)}
          onClose={handleModalClose}
          errorMessage={errorMessage}
        />
      )}
    </>
  )
})

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 320px))',
    gap: 20,
    justifyContent: 'start',
    justifyItems: 'start',
  },
}
